import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  AlertCircle,
  Plus,
  Stethoscope,
  X,
  Trash2,
  Mail,
  Pencil,
} from "lucide-react";
import {
  adminListDoctors,
  adminCreateDoctor,
  adminUpdateDoctor,
  adminDeleteDoctor,
} from "../lib/api";

function DoctorForm({ pin, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: initial?.name || "",
    email: initial?.email || "",
    specialty: initial?.specialty || "",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (isEdit) {
        const payload = {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          specialty: form.specialty.trim() || null,
        };
        if (form.password.trim()) payload.password = form.password;
        await adminUpdateDoctor(pin, initial.id, payload);
      } else {
        await adminCreateDoctor(pin, {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          specialty: form.specialty.trim() || null,
          password: form.password,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not save";
      setError(typeof detail === "string" ? detail : "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--lc-ink)]/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md bg-white border border-[var(--lc-line)] rounded-md p-6 lg:p-8"
        data-testid="doctor-form-modal"
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="eyebrow text-[var(--lc-moss)]">{isEdit ? "Edit doctor" : "Add partner doctor"}</div>
            <h3 className="font-serif-display text-2xl mt-1">
              {isEdit ? initial.name : "New account"}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--lc-muted)] hover:text-red-700" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-4">
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-1.5">Name</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Dr. Jane Zimmermann"
              className="w-full px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm focus:outline-none focus:border-[var(--lc-ink)]"
              data-testid="doctor-form-name"
            />
          </div>
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-1.5">Email</label>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="w-full px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm focus:outline-none focus:border-[var(--lc-ink)]"
              data-testid="doctor-form-email"
            />
          </div>
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-1.5">Specialty (optional)</label>
            <input
              value={form.specialty}
              onChange={(e) => setForm((f) => ({ ...f, specialty: e.target.value }))}
              placeholder="Endocrinology, GP, Diabetology..."
              className="w-full px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm focus:outline-none focus:border-[var(--lc-ink)]"
              data-testid="doctor-form-specialty"
            />
          </div>
          <div>
            <label className="eyebrow text-[var(--lc-muted)] block mb-1.5">
              {isEdit ? "New password (leave blank to keep current)" : "Password (min 6 chars)"}
            </label>
            <input
              type="password"
              required={!isEdit}
              minLength={isEdit ? 0 : 6}
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="w-full px-3 py-2.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-sm focus:outline-none focus:border-[var(--lc-ink)]"
              data-testid="doctor-form-password"
            />
          </div>
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm" data-testid="doctor-form-error">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2.5 border border-[var(--lc-line)] text-[11px] font-bold tracking-[0.14em] uppercase hover:border-[var(--lc-ink)]">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--lc-ink)] text-white text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-moss-dark)] disabled:opacity-50"
            data-testid="doctor-form-submit"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            {isEdit ? "Save changes" : "Create account"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminDoctorsPanel({ pin }) {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | {id,...} (edit)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDoctors(await adminListDoctors(pin));
    } finally {
      setLoading(false);
    }
  }, [pin]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (d) => {
    if (!window.confirm(`Remove ${d.name}'s access? This cannot be undone.`)) return;
    await adminDeleteDoctor(pin, d.id);
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-doctors-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-[var(--lc-muted)]">
          {doctors.length} partner {doctors.length === 1 ? "physician" : "physicians"} · each has their own secure login at <code className="text-[var(--lc-ink)]">/doctor</code>
        </div>
        <button
          onClick={() => setEditing({})}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[var(--lc-ink)] text-white text-[11px] font-bold tracking-[0.14em] uppercase hover:bg-[var(--lc-moss-dark)]"
          data-testid="doctor-add-open"
        >
          <Plus className="h-3.5 w-3.5" /> Add doctor
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-[var(--lc-line)] rounded-md p-8 flex items-center gap-2 text-sm text-[var(--lc-muted)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
        </div>
      ) : doctors.length === 0 ? (
        <div className="bg-white border border-[var(--lc-line)] rounded-md p-10 text-center">
          <Stethoscope className="h-7 w-7 mx-auto text-[var(--lc-muted)] mb-2" />
          <div className="font-serif-display text-xl">No doctors yet</div>
          <p className="text-sm text-[var(--lc-muted)] mt-1">
            Add your partner physician so they can upload lab results to each member.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[var(--lc-line)] rounded-md divide-y divide-[var(--lc-line)]">
          {doctors.map((d) => (
            <article key={d.id} className="p-5 flex items-center justify-between gap-4" data-testid={`admin-doctor-row-${d.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif-display text-lg leading-tight">{d.name}</span>
                  {d.specialty && (
                    <span className="eyebrow px-2 py-0.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-[10px]">
                      {d.specialty}
                    </span>
                  )}
                  {!d.active && (
                    <span className="eyebrow px-2 py-0.5 bg-red-50 border border-red-200 text-red-700 rounded-sm text-[10px]">
                      disabled
                    </span>
                  )}
                </div>
                <a href={`mailto:${d.email}`} className="text-xs text-[var(--lc-muted)] mt-0.5 inline-flex items-center gap-1 hover:text-[var(--lc-ink)]">
                  <Mail className="h-3 w-3" /> {d.email}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditing(d)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-[var(--lc-line)] text-[11px] font-bold tracking-[0.14em] uppercase hover:border-[var(--lc-ink)]"
                  data-testid={`admin-doctor-edit-${d.id}`}
                >
                  <Pencil className="h-3 w-3" /> Edit
                </button>
                <button
                  onClick={() => remove(d)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-[var(--lc-line)] text-[11px] font-bold tracking-[0.14em] uppercase hover:border-red-500 hover:text-red-700"
                  data-testid={`admin-doctor-delete-${d.id}`}
                >
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing !== null && (
        <DoctorForm
          pin={pin}
          initial={editing && editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
